import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import {
  API,
  fetchWithTimeout,
  tokenUtils,
  setSessionExpiredHandler,
  refreshAccessToken,
} from '../config';

// Stripe account status enum matching backend
export type StripeAccountStatus =
  | 'not_connected'
  | 'pending'
  | 'active'
  | 'restricted'
  | 'disabled';

interface User {
  id: number;
  email: string;
  full_name: string | null;
  is_active: boolean;
  is_superuser: boolean;
  // Stripe Connect fields
  stripe_account_status: StripeAccountStatus;
  stripe_onboarding_complete: boolean;
  stripe_charges_enabled: boolean;
  stripe_payouts_enabled: boolean;
  stripe_connected_at: string | null;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Handle session expiration - redirect to login with message
  const handleSessionExpired = useCallback(() => {
    tokenUtils.clearTokens();
    setToken(null);
    setUser(null);
    // Redirect to login with session_expired flag
    window.location.href = '/login?session_expired=true';
  }, []);

  // Register the session expired handler
  useEffect(() => {
    setSessionExpiredHandler(handleSessionExpired);
  }, [handleSessionExpired]);

  // Set up automatic token refresh
  useEffect(() => {
    if (!token) return;

    // Calculate when to refresh (5 minutes before expiry)
    const scheduleRefresh = () => {
      const expiry = tokenUtils.getTokenExpiry();
      if (!expiry) return null;

      // Refresh 5 minutes before expiry
      const refreshTime = expiry - Date.now() - 5 * 60 * 1000;

      if (refreshTime <= 0) {
        // Token is already expired or expiring very soon, refresh now
        refreshAccessToken().then(success => {
          if (success) {
            setToken(tokenUtils.getAccessToken());
          } else {
            handleSessionExpired();
          }
        });
        return null;
      }

      return setTimeout(async () => {
        const success = await refreshAccessToken();
        if (success) {
          setToken(tokenUtils.getAccessToken());
          // Schedule next refresh
          scheduleRefresh();
        } else {
          handleSessionExpired();
        }
      }, refreshTime);
    };

    const timerId = scheduleRefresh();
    return () => {
      if (timerId) clearTimeout(timerId);
    };
  }, [token, handleSessionExpired]);

  // Load token from localStorage on mount
  useEffect(() => {
    const loadAuth = async () => {
      const storedToken = tokenUtils.getAccessToken();
      if (storedToken) {
        // Check if token is still valid
        if (tokenUtils.isTokenExpiringSoon(0)) {
          // Token expired, try to refresh
          const refreshed = await refreshAccessToken();
          if (!refreshed) {
            tokenUtils.clearTokens();
            setIsLoading(false);
            return;
          }
        }

        const currentToken = tokenUtils.getAccessToken();
        setToken(currentToken);

        // Fetch user info with the stored token
        try {
          const response = await fetchWithTimeout(API.AUTH.ME, {
            headers: {
              'Authorization': `Bearer ${currentToken}`,
            },
          });

          if (response.ok) {
            const userData = await response.json();
            setUser(userData);
          } else {
            // Token invalid, clear it
            tokenUtils.clearTokens();
            setToken(null);
          }
        } catch (error) {
          console.error('Error loading user:', error);
          tokenUtils.clearTokens();
          setToken(null);
        }
      }
      setIsLoading(false);
    };

    loadAuth();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await fetchWithTimeout(API.AUTH.LOGIN, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Login failed');
      }

      const data = await response.json();

      // Store both tokens
      tokenUtils.setTokens(data.access_token, data.refresh_token, data.expires_in);
      setToken(data.access_token);

      // Fetch user info
      const userResponse = await fetchWithTimeout(API.AUTH.ME, {
        headers: {
          'Authorization': `Bearer ${data.access_token}`,
        },
      });

      if (userResponse.ok) {
        const userData = await userResponse.json();
        setUser(userData);
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = async () => {
    // Try to logout on backend (revoke refresh token)
    const refreshToken = tokenUtils.getRefreshToken();
    const accessToken = tokenUtils.getAccessToken();

    if (refreshToken && accessToken) {
      try {
        await fetchWithTimeout(API.AUTH.LOGOUT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ refresh_token: refreshToken }),
        });
      } catch (error) {
        // Ignore errors - we'll clear local tokens anyway
        console.error('Logout error:', error);
      }
    }

    // Clear local tokens
    tokenUtils.clearTokens();
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    const currentToken = tokenUtils.getAccessToken();
    if (!currentToken) return;

    try {
      const response = await fetchWithTimeout(API.AUTH.ME, {
        headers: {
          'Authorization': `Bearer ${currentToken}`,
        },
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      }
    } catch (error) {
      console.error('Error refreshing user:', error);
    }
  };

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated: !!token && !!user,
    isLoading,
    login,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
